import React, { useState, useContext, useEffect } from 'react'
import ResponsiveAppBar from '../../components/Navbar'
import { getInfo, remove, download } from '../../services/invoice'
import { UserContext } from '../../context/UserProvider.jsx'
import { useNavigate } from 'react-router-dom'
import { Grid, Container, Paper, Button, Tabs, Tab, Box, Chip, Alert } from '@mui/material'
import { useParams } from 'react-router-dom'
import { DataGrid } from '@mui/x-data-grid'
import PaymentHistory from '../AccountsReceivable/PaymentHistory'
import { canAccessAR } from '../../services/ar'
import { money } from '../AccountsReceivable/utils'

const App = () => {
  const navigate = useNavigate()
  const [itemData, setItemData] = useState(null)
  const { token, access, userType } = useContext(UserContext)
  const [activeTab, setActiveTab] = useState('items')
  const [deleteError, setDeleteError] = useState('')
  const showPayments = canAccessAR(access, userType)
  const { id } = useParams()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = () => {
    getInfo(token, id)
      .then((res) => {
        setItemData({
          ...res.data,
          ...res.data.raw,
        })
      })
      .catch((err) => {
        alert(err.response?.data?.message || 'Invoice could not be loaded. Please try again.')
      })
    }

    const handleDownload = () => {
      download(token, id).then((res) => {
        if (res?.url) {
          window.open(res.url, '_blank')
        }
      })
    }
    
    const handleDelete = () => {
      if (itemData?.paymentSummary?.has_payment_records) {
        const status = itemData.paymentSummary.status || 'Accounts Receivable'
        setDeleteError(
          `Invoice cannot be deleted. This invoice is ${status} and has an Accounts Receivable record. Its payment and audit history must be retained.`
        )
        return
      }

      const conVal = window.confirm('Are you sure you want to delete this invoice?');

      if (conVal) {
        setDeleteError('')
        remove(token, id)
        .then((res) => {
          navigate(-1)
        })
        .catch((err) => {
          setDeleteError(
            err?.response?.data?.message
              || 'Unable to delete this invoice. Please try again.'
          )
        })
      }
    }

  if (!itemData) {
    return null
  }

  const deletionBlocked = Boolean(itemData.paymentSummary?.has_payment_records)
 
  return (
    <>
      <ResponsiveAppBar />
      <Container maxWidth={'xl'}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <h1>Invoice Information</h1>
          </Grid>
          <Grid item xs={8}>
            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                navigate(-1)
              }}
              style={{
                marginTop: '20px',
                backgroundColor: '#3675af',
                color: 'white',
                height: '55px',
                float: 'right',
              }}
            >
              Back
            </Button>
            <Button
                  variant="contained"
                  disableElevation
                  onClick={() => handleDownload()}
                  style={{
                    marginTop: '20px',
                    backgroundColor: '#3675af',
                    color: 'white',
                    height: '55px',
                    float: 'right',
                    marginRight: '10px',
                  }}
                >
                  Download
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  title={deletionBlocked ? 'This invoice has Accounts Receivable history.' : 'Delete invoice'}
                  onClick={() => handleDelete()}
                  style={{
                    marginTop: '20px',
                    backgroundColor: 'red',
                    color: 'white',
                    height: '55px',
                    float: 'right',
                    marginRight: '10px',
                  }}
                >
                  Delete
                </Button>
          </Grid>
        </Grid>
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setDeleteError('')}>
            {deleteError}
          </Alert>
        )}
        <div
          style={{
            minHeight: '80vh',
            width: '100%',
            backgroundColor: '#ffff',
            marginTop: '20px',
          }}
        >
          <Paper elevation={0} sx={{ padding: 3 }}>
            <Review data={itemData} />
            <Tabs value={showPayments ? activeTab : 'items'} onChange={(_, value) => setActiveTab(value)} aria-label="Invoice details" sx={{ mt: 3, mb: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 } }}>
              <Tab label="Item" value="items" id="invoice-items-tab" aria-controls="invoice-items-panel" />
              {showPayments && <Tab label="Payment history" value="payments" id="invoice-payments-tab" aria-controls="invoice-payments-panel" />}
            </Tabs>
            <Box role="tabpanel" id="invoice-items-panel" aria-labelledby="invoice-items-tab" hidden={showPayments && activeTab !== 'items'}>
              {(!showPayments || activeTab === 'items') && <ProductList itemList={itemData.itemSummary ?? []} />}
            </Box>
            {showPayments && <Box role="tabpanel" id="invoice-payments-panel" aria-labelledby="invoice-payments-tab" hidden={activeTab !== 'payments'}>
              {activeTab === 'payments' && <div className="ar-card"><PaymentHistory invoiceId={Number(id)} /></div>}
            </Box>}
          </Paper>
        </div>
      </Container>
    </>
  )
}

export const Review = ({ data }) => {
  const payment = data.paymentSummary
  const customer = data.customerAccount ?? {}
  const statusColor = payment?.status === 'Paid' ? 'success' : payment?.status === 'Partial' ? 'warning' : 'error'

  return (
    <>
      <table className="table-information">
        <tbody>
          <tr>
            <td className="caption">
              <strong>Sales Person: </strong>
            </td>
            <td>{data['sales-person-name'] ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Account Number: </strong>
            </td>
            <td>{customer['account-number'] ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Company Name: </strong>
            </td>
            <td>{customer['company-name'] ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Address Line 1: </strong>
            </td>
            <td>{customer['address-line-1'] ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Address Line 2: </strong>
            </td>
            <td>{customer['address-line-2'] ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Phone Number: </strong>
            </td>
            <td>{customer['phone'] ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Email Address: </strong>
            </td>
            <td>{customer['email'] ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Sub-Total: </strong>
            </td>
            <td>$ {data.subTotal ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Delivery Fee: </strong>
            </td>
            <td>$ {data.deliveryFee ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Tax: </strong>
            </td>
            <td>
              $ {data.tax ?? '0.00'}
            </td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Total: </strong>
            </td>
            <td>$ {data.grandTotal ?? ''}</td>
          </tr>
          {payment && <>
            <tr>
              <td className="caption">
                <strong>Total Paid: </strong>
              </td>
              <td>{money(payment.paid_cents)}</td>
            </tr>
            <tr>
              <td className="caption">
                <strong>Total Outstanding: </strong>
              </td>
              <td>{money(payment.outstanding_cents)}</td>
            </tr>
            <tr>
              <td className="caption">
                <strong>Invoice Status: </strong>
              </td>
              <td><Chip color={statusColor} label={payment.status} size="small" variant="outlined" sx={{ fontWeight: 600 }} /></td>
            </tr>
          </>}
        </tbody>
      </table>
    </>
  )
}

const ProductList = ({ itemList }) => {
  const columns = [
    { field: 'UPC', headerName: 'UPC', flex: 1 },
    { field: 'Item', headerName: 'Item', width: 70 },
    { field: 'Description', headerName: 'Description', flex: 1 },
    { field: 'New Price', headerName: 'Price', flex: 1 },
    { field: 'Qty', headerName: 'Qty', flex: 1 },
    { field: 'C/U', headerName: 'C/U', flex: 1 },
    { field: 'New Total', headerName: 'Total', flex: 1 },
    { field: 'New Tax', headerName: 'Tax', flex: 1 },
  ]

  return (
    <>
      <div style={{ marginTop: '10px', marginBottom: '10px' }}>
        <DataGrid
          autoHeight
          rows={itemList.map((item, index) => {
            return {
              id: index,
              ...item,
            }
          })}
          columns={columns}
        />
      </div>
    </>
  )
}

export default App
